import { ThirdPartyConfigsSync as ThirdPartyConfigs } from '@atlassian/rovo-connectors-config/configs-sync';
import { buildAtlAttributionHeaders } from '@atlassian/search-client/atl-attribution';
import { thirdPartyRepositoryFilterOptionsQuery } from '@atlassian/search-client/search-page';
import { getCloudIdARIs } from '@atlassian/search-common/get-cloud-id-ari';

import { DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI } from '../../../../../common/constants/default-cloud-graph-devinfo-repository-entity-ati';
import type { CloudConfig } from '../../../../../common/constants/filters/cloud-config/types';
import type {
	SelectFilterMonad,
	SelectFilterState,
} from '../../../../../common/constants/filters/select-filter/types';
import { NounSubtypeKey } from '../../../../../common/constants/filters/universal-type';
import { is3pProductKey, type ProductKeys } from '../../../../../common/constants/product-keys';
import { type SelectFilterOption } from '../../../../../common/ui/filters/select-filter';
import { getProducts3PFromConfigForFilter } from '../../../../../common/utils/filters';
import { createMultiSelectFilter } from '../../../../filters/multi-select-filter';

const products3PFromConfig = getProducts3PFromConfigForFilter('repository');

const FETCH_LIMIT = 30;

/**
 * Calls AGG to fetch the user's accessible repositories for the currently-selected 3P datasource.
 */
const fetchRepositoryOptions = async (
	config: CloudConfig,
	query: string = '',
): Promise<SelectFilterOption[]> => {
	const { cloudId, product, primaryProduct, activationId } = config;

	const integrationAri =
		config.selectedDatasource?.integrationAri ??
		(product && is3pProductKey(product)
			? ThirdPartyConfigs.get(product)?.integrationAri
			: undefined);

	if (!cloudId || !product || !integrationAri) {
		return [];
	}

	const headers = buildAtlAttributionHeaders({
		cloudId,
		activationId,
		product: primaryProduct,
	});

	const response = await thirdPartyRepositoryFilterOptionsQuery({
		variables: {
			query,
			cloudIdARI: getCloudIdARIs(cloudId, primaryProduct),
			limit: FETCH_LIMIT,
			entities: DEFAULT_CLOUD_GRAPH_DEVINFO_REPOSITORY_ENTITY_ATI,
			product,
			integration: integrationAri,
			subtypes: [NounSubtypeKey.Repository],
		},
		aggAbsoluteUrl: config.aggAbsoluteUrl,
		headers,
	});

	return (
		response?.data?.search?.search?.edges
			?.map(({ node }): SelectFilterOption | undefined => {
				const name = node.entity?.name;
				if (!name) {
					return undefined;
				}
				return {
					value: name,
					trackingKey: name,
					queryParamValue: name,
					label: name,
					product: config.product,
					isDefault: false,
					isSelected: false,
					isSuggested: false,
				};
			})
			.filter((option): option is SelectFilterOption => option !== undefined) ?? []
	);
};

/**
 * Initial / pre-query options shown when the filter dropdown is first opened.
 */
const getInitialOptions = async (config: CloudConfig): Promise<SelectFilterOption[]> =>
	fetchRepositoryOptions(config);

/**
 * URL-hydration fetch. Resolves `?repository=...` values into
 * `SelectFilterOption`.
 */
const fetchQueryParams = async (
	queryParams: string[],
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	if (queryParams.length === 0) {
		return [];
	}
	const allOptions = await fetchRepositoryOptions(config, queryParams.join(' '));

	const matched: SelectFilterOption[] = allOptions
		.filter((option) => queryParams.some((queryParam) => option.value === queryParam))
		.map((option) => ({ ...option, isSelected: true }));

	if (matched.length === queryParams.length) {
		return matched;
	}

	const matchedValues = new Set(matched.map((option) => option.value));
	const fallback: SelectFilterOption[] = queryParams
		.filter((queryParam) => !matchedValues.has(queryParam))
		.map((queryParam) => ({
			value: queryParam,
			trackingKey: queryParam,
			queryParamValue: queryParam,
			isDefault: false,
			label: queryParam,
			product: config.product,
			isSelected: true,
			isSuggested: false,
		}));

	return [...matched, ...fallback];
};

/**
 * Lookup (typeahead) fetcher.
 */
const fetchLookupOptions = async (
	input: string,
	config: CloudConfig,
): Promise<SelectFilterOption[]> => {
	if (!input) {
		return [];
	}
	return fetchRepositoryOptions(config, input);
};

export const filter: Readonly<{
	id: 'repository';
	type: 'single-select' | 'multi-select';
	queryParams: {
		key: string;
		getState: (
			queryParams: Record<string, string[]>,
			cloudId: string,
		) => SelectFilterState<'repository'>;
	};
	products: ProductKeys[];
	universal: boolean;
	customValueFields?: string[];
	withState: (state: unknown, config: CloudConfig) => SelectFilterMonad<'repository'>;
	getInitialState: (config: CloudConfig) => SelectFilterMonad<'repository'>;
	isCompatible: (filterId?: string) => boolean;
	isSupportCustomValue: boolean;
	isSuggestedFilter?: (primaryProduct?: string) => boolean;
}> = createMultiSelectFilter({
	id: 'repository',
	products: products3PFromConfig,
	alwaysVisible: false,
	universal: false,
	queryParams: {
		key: 'repository',
		fetcher: fetchQueryParams,
	},
	initialOptions: {
		fetcher: getInitialOptions,
	},
	lookup: {
		fetcher: fetchLookupOptions,
	},
});
